/**
 * Scroll Service (Horizontal + Vertical)
 *
 * Provides reusable touch, wheel, and keyboard handling for scroll navigation.
 * Supports both horizontal and vertical modes.
 */

import { useRef, useCallback, useEffect } from "react";

// Default configuration (merged with direction-specific behavior)
const DEFAULT_CONFIG = {
  direction: "horizontal", // "horizontal" | "vertical"
  sensitivity: 60,
  transitionDuration: 400,
  debounceDelay: 80,
  momentum: true,
  snapThreshold: 0.3,
  allowVerticalWheelPassthrough: false,
  // Touch thresholds
  minSwipeDistance: 30,
  maxSwipeTime: 400,
  maxDrift: 60, // perpendicular drift tolerance
  // Wheel accumulation
  wheelAcceleration: 0.8,
  maxMomentumForce: 2.5,
  momentumMultiplier: 3,
};

export const useScrollService = (onNavigate, config = {}, totalItems = 0) => {
  const {
    direction,
    sensitivity,
    transitionDuration,
    debounceDelay,
    momentum,
    allowVerticalWheelPassthrough,
    minSwipeDistance,
    maxSwipeTime,
    maxDrift,
    wheelAcceleration,
    maxMomentumForce,
    momentumMultiplier,
  } = { ...DEFAULT_CONFIG, ...config };

  // State refs
  const isScrollingRef = useRef(false);
  const lastScrollTimeRef = useRef(0);
  const accumulatedDeltaRef = useRef(0);
  const touchStartRef = useRef(null);
  const containerRef = useRef(null);

  // Debounced navigate
  const debouncedNavigate = useCallback(
    (dir, force = 1) => {
      const now = Date.now();

      if (now - lastScrollTimeRef.current < debounceDelay) return;
      if (isScrollingRef.current) return;

      lastScrollTimeRef.current = now;
      isScrollingRef.current = true;

      onNavigate(dir, force);

      setTimeout(() => {
        isScrollingRef.current = false;
        accumulatedDeltaRef.current = 0;
      }, transitionDuration);
    },
    [onNavigate, debounceDelay, transitionDuration]
  );

  // Wheel handler
  const handleWheel = useCallback(
    (e) => {
      const hasHorizontalIntent =
        Math.abs(e.deltaX) > Math.abs(e.deltaY) ||
        (e.shiftKey && e.deltaY !== 0);

      if (
        direction === "horizontal" &&
        allowVerticalWheelPassthrough &&
        !hasHorizontalIntent
      ) {
        accumulatedDeltaRef.current = 0;
        return;
      }

      e.preventDefault();

      const delta =
        direction === "horizontal"
          ? hasHorizontalIntent && e.deltaX === 0
            ? e.deltaY
            : e.deltaX !== 0
            ? e.deltaX
            : e.deltaY // trackpad fallback
          : e.deltaY;

      accumulatedDeltaRef.current += delta * wheelAcceleration;

      if (Math.abs(accumulatedDeltaRef.current) >= sensitivity) {
        const scrollDirection =
          accumulatedDeltaRef.current > 0 ? "next" : "prev";

        const force = Math.min(
          Math.abs(accumulatedDeltaRef.current) / sensitivity,
          2
        );

        debouncedNavigate(scrollDirection, force);
        accumulatedDeltaRef.current = 0;
      }
    },
    [
      direction,
      allowVerticalWheelPassthrough,
      sensitivity,
      wheelAcceleration,
      debouncedNavigate,
    ]
  );

  // Touch handling
  const handleTouchStart = useCallback((e) => {
    const touch = e.touches[0];
    touchStartRef.current = {
      x: touch.clientX,
      y: touch.clientY,
      time: Date.now(),
    };
  }, []);

  const handleTouchEnd = useCallback(
    (e) => {
      if (!touchStartRef.current || isScrollingRef.current) return;

      const touch = e.changedTouches[0];
      const deltaX = touch.clientX - touchStartRef.current.x;
      const deltaY = touch.clientY - touchStartRef.current.y;
      const deltaTime = Date.now() - touchStartRef.current.time;

      const primaryDelta = direction === "horizontal" ? deltaX : deltaY;
      const crossDelta = direction === "horizontal" ? deltaY : deltaX;

      if (
        deltaTime <= maxSwipeTime &&
        Math.abs(primaryDelta) >= minSwipeDistance &&
        Math.abs(crossDelta) <= maxDrift
      ) {
        const scrollDirection = primaryDelta > 0 ? "prev" : "next";

        const distance = Math.abs(primaryDelta);
        const speed = distance / deltaTime;
        const momentumForce = momentum
          ? Math.min(
              speed * momentumMultiplier,
              maxMomentumForce
            )
          : 1;

        debouncedNavigate(scrollDirection, momentumForce);
      }

      touchStartRef.current = null;
    },
    [
      direction,
      maxDrift,
      maxMomentumForce,
      maxSwipeTime,
      minSwipeDistance,
      momentum,
      momentumMultiplier,
      debouncedNavigate,
    ]
  );

  // Keyboard handling
  const handleKeyDown = useCallback(
    (e) => {
      if (isScrollingRef.current) return;

      let scrollDirection = null;

      if (direction === "horizontal") {
        if (e.key === "ArrowRight") scrollDirection = "next";
        else if (e.key === "ArrowLeft") scrollDirection = "prev";
      } else {
        if (e.key === "ArrowDown" || e.key === "PageDown" || e.key === " ")
          scrollDirection = "next";
        else if (e.key === "ArrowUp" || e.key === "PageUp")
          scrollDirection = "prev";
      }

      if (scrollDirection) {
        e.preventDefault();
        debouncedNavigate(scrollDirection, 1);
      }
    },
    [direction, debouncedNavigate]
  );

  // Attach listeners
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    container.addEventListener("wheel", handleWheel, { passive: false });
    container.addEventListener("keydown", handleKeyDown);

    if (container.tabIndex === -1) {
      container.tabIndex = 0;
    }

    container.addEventListener("touchstart", handleTouchStart, {
      passive: true,
    });
    container.addEventListener("touchend", handleTouchEnd, { passive: true });

    return () => {
      container.removeEventListener("wheel", handleWheel);
      container.removeEventListener("keydown", handleKeyDown);
      container.removeEventListener("touchstart", handleTouchStart);
      container.removeEventListener("touchend", handleTouchEnd);
    };
  }, [handleWheel, handleKeyDown, handleTouchStart, handleTouchEnd]);

  return {
    containerRef,
    isScrollingRef,
    navigateTo: debouncedNavigate,
  };
};

/**
 * Utility functions
 */
export const NavigationUtils = {
  /**
   * Get next index with wrapping
   */
  getNextIndex: (currentIndex, totalItems, direction = "next") => {
    if (direction === "next") {
      return currentIndex === totalItems - 1 ? 0 : currentIndex + 1;
    } else {
      return currentIndex === 0 ? totalItems - 1 : currentIndex - 1;
    }
  },

  /**
   * Get visible items (prev, current, next) — for carousel-like components
   */
  getVisibleItems: (items, currentIndex) => {
    if (!items || items.length === 0) return [];

    const totalItems = items.length;
    const prevIndex = currentIndex === 0 ? totalItems - 1 : currentIndex - 1;
    const nextIndex = currentIndex === totalItems - 1 ? 0 : currentIndex + 1;

    return [
      { ...items[prevIndex], position: "previous", index: prevIndex },
      { ...items[currentIndex], position: "current", index: currentIndex },
      { ...items[nextIndex], position: "next", index: nextIndex },
    ];
  },

  /**
   * Container styles
   */
  getContainerStyles: (transitionDuration = 400, direction = "horizontal") => ({
    touchAction: direction === "horizontal" ? "pan-y" : "pan-x",
    userSelect: "none",
    outline: "none",
    "--transition-duration": `${transitionDuration}ms`,
  }),
};

const scrollService = {
  useScrollService,
  NavigationUtils,
  DEFAULT_CONFIG,
};

export default scrollService;
